import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpen, Calendar, FileText, User, ExternalLink } from 'lucide-react';
import { research } from '../data/research';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const ResearchDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const researchItem = research.find(r => r.id === parseInt(id));

  const handleBackClick = () => {
    navigate('/#research');
  };

  if (!researchItem) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="text-center">
          <h2 className="mb-4 text-2xl font-bold text-white">Research Not Found</h2>
          <button onClick={() => navigate('/')} className="text-blue-400 hover:text-blue-300">
            Go Back Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <div className="px-4 pb-20 pt-24 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={handleBackClick}
            className="mb-8 flex items-center gap-2 text-gray-400 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Publications
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md"
          >
            <div className="p-6 md:p-8">
              {/* Header Section */}
              <div className="mb-6 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="rounded-lg bg-gradient-to-br from-blue-500/20 to-red-500/20 p-2">
                      {researchItem.type === 'Article' ? (
                        <FileText className="h-5 w-5 text-blue-400" />
                      ) : (
                        <BookOpen className="h-5 w-5 text-blue-400" />
                      )}
                    </div>
                    <span className="rounded-full border border-blue-500/30 bg-blue-500/20 px-3 py-1 text-xs text-blue-400">
                      {researchItem.type}
                    </span>
                    {researchItem.fullTextAvailable && (
                      <span className="rounded-full border border-green-500/30 bg-green-500/20 px-3 py-1 text-xs text-green-400">
                        Full-text available
                      </span>
                    )}
                  </div>
                  <h1 className="mb-4 text-2xl font-bold leading-tight text-white md:text-3xl">
                    {researchItem.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{researchItem.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{researchItem.author}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              <div className="space-y-6">
                <div>
                  <h2 className="mb-2 text-lg font-semibold text-white">Abstract</h2>
                  <p className="leading-relaxed text-gray-300">{researchItem.description}</p>
                </div>

                <div>
                  <h2 className="mb-2 text-lg font-semibold text-white">Description</h2>
                  <p className="leading-relaxed text-gray-300">{researchItem.longDescription}</p>
                </div>

                {researchItem.url && (
                  <div className="border-t border-white/10 pt-4">
                    <a
                      href={researchItem.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/50 transition-all duration-300 hover:scale-105 hover:shadow-blue-500/70"
                    >
                      View on ResearchGate
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResearchDetails;
